import React, { useState } from 'react'
import Dialog from 'components/dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import { TextFieldWithError } from 'components/form-util'
import CurrencyField from 'components/form-util/currency'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import { gql, useMutation } from '@apollo/client'
import CircularProgress from '@material-ui/core/CircularProgress'
import { formatRefundErrorMessage } from '../../transaction-helpers'
import { useStyles } from './styles'

const ISSUE_REFUND = gql`
  mutation issueRefund($input: PGACoachRefundInput!) {
    issueRefundPGACoach(input: $input) {
      success
      message
    }
  }
`

export const formatCurrency = amount => `$${(amount / 100).toFixed(2)}`

const schema = leftToRefund =>
  yup
    .object({
      amount: yup
        .number()
        .moreThan(0)
        .max(
          leftToRefund,
          `Amount is higher than what is left to refund (${formatCurrency(
            leftToRefund * 100,
          )})`,
        )
        .typeError('Amount must be a valid number')
        .label('Amount')
        .required(),
      reason: yup
        .string()
        .label('Reason')
        .required(),
    })
    .required()

const RefundDialog = ({
  registrationPaymentId,
  leftToRefund,
  trigger,
  onSuccess,
}) => {
  const classes = useStyles()
  const [submitError, setSubmitError] = useState('')
  const [issueRefundMutation, { loading, error, reset }] = useMutation(
    ISSUE_REFUND,
    {
      refetchQueries: ['GetRegistrationPayments'],
      awaitRefetchQueries: false,
    },
  )

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      amount: leftToRefund,
      reason: '',
    },
    resolver: yupResolver(schema(leftToRefund)),
  })

  const clearDialogState = () => {
    reset()
    setSubmitError('')
  }

  const handleClose = closeDialog => {
    clearDialogState()
    closeDialog()
  }

  const onSubmit = closeDialog => async ({ amount, reason }) => {
    setSubmitError('')

    try {
      const { data } = await issueRefundMutation({
        variables: {
          input: {
            registrationPaymentId,
            amountInCents: parseInt(amount * 100, 10),
            reason,
          },
        },
      })

      if (!data?.issueRefundPGACoach?.success) {
        setSubmitError(
          data?.issueRefundPGACoach?.message ||
            'Unable to process refund. Please try again.',
        )
        return
      }

      if (onSuccess) {
        await onSuccess()
      }

      clearDialogState()
      closeDialog()
    } catch {
      // Apollo surfaces request errors through the error hook.
    }
  }

  const errorMessage = submitError || formatRefundErrorMessage(error)

  return (
    <Dialog
      trigger={trigger}
      showCloseButton={true}
      fullWidth
      maxWidth={'xs'}
      onClose={clearDialogState}
    >
      {({ closeDialog }) => (
        <>
          <DialogTitle>Refund</DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={12}
              >
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit(closeDialog))}>
                <Box className={classes.formStack}>
                  <CurrencyField
                    name="amount"
                    label="Refund Amount"
                    inputRef={register}
                    error={!!errors.amount}
                    helperText={errors?.amount?.message}
                    required
                    includeThousandsSeparator={false}
                    className={classes.fullWidthField}
                  />
                  <TextFieldWithError
                    label="Reason"
                    name="reason"
                    inputRef={register}
                    errors={errors}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    required
                    inputProps={{ 'aria-label': 'reason' }}
                  />
                </Box>

                {errorMessage ? (
                  <Typography color="error" variant="body2">
                    {errorMessage}
                  </Typography>
                ) : null}

                <Box className={classes.actions}>
                  <Button
                    type="button"
                    variant="text"
                    onClick={() => handleClose(closeDialog)}
                  >
                    Close
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    Refund
                  </Button>
                </Box>
              </form>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  )
}

export default RefundDialog
